import React, { useState, useRef, useLayoutEffect } from "react";
import ExpandIcon from "@material-ui/icons/UnfoldMoreRounded";
import CollapseIcon from "@material-ui/icons/UnfoldLessRounded";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/EditOutlined";
import ViewIcon from "@material-ui/icons/VisibilityOutlined";
import BoldIcon from "@material-ui/icons/FormatBold";
import BulletIcon from "@material-ui/icons/FormatListBulleted";
import NumberIcon from "@material-ui/icons/FormatListNumbered";
import QuoteIcon from "@material-ui/icons/FormatQuote";

// altura da caixa recolhida (Editar e Visualizar). Texto maior que isso fica
// recolhido com esmaecido + "Mostrar tudo" (padrão "mostrar mais" do GitHub),
// para a persona longa não empurrar o resto da configuração para baixo.
const COLLAPSED = 300;

// Editor da PERSONA (instruções da IA). Texto livre em markdown, 2 modos:
// Editar (markdown cru, mono) e Visualizar (formatado sóbrio). Fonte da verdade =
// o markdown cru; o preview é derivado. A IA recebe o markdown PURO como prompt.

// --- parser markdown simples (h2/h3, ul/ol, callout, **bold**) ---
const inline = text => {
  const out = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<b key={k++}>{m[1]}</b>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const parse = md => {
  const lines = String(md || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  const isSpecial = l => /^(##\s|###\s|\s*[-*]\s|\s*\d+\.\s|>)/.test(l);
  while (i < lines.length) {
    const l = lines[i];
    if (/^##\s+/.test(l)) { blocks.push({ t: "h2", text: l.replace(/^##\s+/, "") }); i++; continue; }
    if (/^###\s+/.test(l)) { blocks.push({ t: "h3", text: l.replace(/^###\s+/, "") }); i++; continue; }
    if (/^\s*[-*]\s+/.test(l)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, "")); i++; }
      blocks.push({ t: "ul", items }); continue;
    }
    if (/^\s*\d+\.\s+/.test(l)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
      blocks.push({ t: "ol", items }); continue;
    }
    if (/^>\s?/.test(l)) {
      const parts = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { parts.push(lines[i].replace(/^>\s?/, "")); i++; }
      blocks.push({ t: "callout", text: parts.join("\n") }); continue;
    }
    if (l.trim() === "") { i++; continue; }
    const parts = [];
    while (i < lines.length && lines[i].trim() !== "" && !isSpecial(lines[i])) { parts.push(lines[i]); i++; }
    blocks.push({ t: "p", text: parts.join("\n") });
  }
  return blocks;
};

const useStyles = makeStyles(theme => ({
  wrap: { width: "100%" },
  bar: { display: "flex", alignItems: "center", marginBottom: 8 },
  label: { fontSize: 13, fontWeight: 600, color: theme.app.ink },
  grow: { flex: 1 },
  seg: { display: "inline-flex", background: theme.app.chip || "#EEF0F3", borderRadius: 8, padding: 2 },
  segBtn: {
    display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "transparent",
    fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: theme.app.textTertiary, cursor: "pointer",
    padding: "5px 10px", borderRadius: 6, "& svg": { fontSize: 15 },
  },
  segOn: { background: "#fff", color: theme.app.ink, boxShadow: "0 1px 2px rgba(16,20,26,.12)" },

  box: { border: `1px solid ${theme.app.borderInput || "#D8DBDF"}`, borderRadius: 10, overflow: "hidden", background: "#fff" },
  boxFocus: { borderColor: theme.app.dark || "#2C353D", boxShadow: "0 0 0 3px rgba(44,53,61,.08)" },

  toolbar: { display: "flex", alignItems: "center", gap: 2, padding: "6px 8px", borderBottom: `1px solid ${theme.app.lineSoft || "#EEF0F3"}`, flexWrap: "wrap" },
  tbtn: {
    border: "none", background: "transparent", cursor: "pointer", color: theme.app.textSecondary,
    fontFamily: theme.app.heading, fontWeight: 700, fontSize: 12, padding: "4px 7px", borderRadius: 6, minWidth: 26,
    display: "inline-flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 },
    "&:hover": { background: theme.app.hover || "#F4F6F8", color: theme.app.ink },
  },
  tsep: { width: 1, height: 18, background: theme.app.lineSoft || "#EEF0F3", margin: "0 4px" },

  ta: {
    display: "block", width: "100%", boxSizing: "border-box", border: "none", outline: "none", resize: "none",
    minHeight: 220, padding: "12px 14px", background: "transparent", overflowY: "auto",
    fontFamily: theme.app.mono, fontSize: 13, lineHeight: 1.6, color: theme.app.ink,
  },

  // preview sóbrio
  viewWrap: { position: "relative", overflow: "hidden" },
  fade: { position: "absolute", left: 0, right: 0, bottom: 0, height: 72, pointerEvents: "none", background: "linear-gradient(to bottom, rgba(255,255,255,0), #fff)" },
  more: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5, width: "100%", border: "none",
    borderTop: `1px solid ${theme.app.lineSoft || "#EEF0F3"}`, background: theme.app.chatBg || "#F7F8FA",
    fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: theme.app.textSecondary, cursor: "pointer", padding: "7px 0",
    "& svg": { fontSize: 16 }, "&:hover": { color: theme.app.ink, background: theme.app.hover || "#F1F4F2" },
  },
  view: { padding: "14px 16px", minHeight: 220, fontSize: 13.5, lineHeight: 1.62, color: theme.app.textSecondary },
  h2: { display: "flex", alignItems: "center", gap: 8, fontFamily: theme.app.heading, fontWeight: 600, fontSize: 15, color: theme.app.ink, margin: "16px 0 8px", paddingBottom: 6, borderBottom: `1px solid ${theme.app.lineSoft || "#EEF0F3"}`, "&:first-child": { marginTop: 0 } },
  h2mark: { width: 7, height: 7, borderRadius: 2, background: theme.app.dark || "#2C353D", flexShrink: 0 },
  h3: { fontFamily: theme.app.heading, fontWeight: 600, fontSize: 13.5, color: theme.app.ink, margin: "14px 0 6px" },
  p: { margin: "0 0 10px", whiteSpace: "pre-wrap", "& b": { color: theme.app.ink } },
  ul: { margin: "0 0 10px", padding: 0, listStyle: "none" },
  li: { position: "relative", paddingLeft: 16, marginBottom: 5, "&::before": { content: '""', position: "absolute", left: 3, top: 8, width: 5, height: 5, borderRadius: "50%", background: theme.app.textTertiary }, "& b": { color: theme.app.ink } },
  olrow: { display: "flex", gap: 8, marginBottom: 5, "& b": { color: theme.app.ink } },
  olnum: { flexShrink: 0, minWidth: 20, height: 20, borderRadius: 6, background: theme.app.chip || "#EEF0F3", color: theme.app.ink2 || "#5B6470", fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 1 },
  callout: { background: "#FBF6EC", border: "1px solid #EBDBB8", borderRadius: 8, padding: "10px 12px", margin: "0 0 10px", color: "#7a5a1e", whiteSpace: "pre-wrap", "& b": { color: "#5c440f" } },
  empty: { color: theme.app.textTertiary, fontStyle: "italic" },

  hint: { fontSize: 12, color: theme.app.textTertiary, marginTop: 6 },

  // chip "IA escrevendo…" (substitui o Editar/Visualizar enquanto o copiloto escreve)
  writingChip: {
    display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600,
    color: theme.app.dark || "#2C353D", background: theme.app.chip || "#EEF0F3",
    borderRadius: 8, padding: "5px 11px",
  },
  writingDot: {
    width: 7, height: 7, borderRadius: "50%", background: "#86C240", flexShrink: 0,
    animation: "$writingPulse 1s ease-in-out infinite",
  },
  "@keyframes writingPulse": { "0%,100%": { opacity: 0.35, transform: "scale(0.85)" }, "50%": { opacity: 1, transform: "scale(1)" } },
}));

const PersonaEditor = ({
  value,
  onChange,
  label = "Instruções da persona",
  defaultMode = "edit",
  readOnly = false,
  // textos parametrizáveis (defaults = editor de PERSONA; o FlowNotesModal passa
  // versões próprias de "notas do fluxo" para não vazar linguagem de ligação/IA)
  placeholder = "Descreva como a IA deve se comportar — quem ela é, o objetivo da ligação, o tom e o que evitar. Use ## para títulos, - para listas e > para destaques.",
  emptyText = "Nada escrito ainda. Volte para Editar e descreva a persona.",
  hintEdit = "Texto livre com markdown (## título, - lista, **negrito**, > destaque). A IA recebe exatamente este texto.",
  hintView = "Prévia formatada. A IA segue estas instruções durante toda a ligação.",
  hintWriting = "A IA está escrevendo as instruções… você poderá editar quando ela terminar.",
}) => {
  const classes = useStyles();
  const [mode, setMode] = useState(defaultMode);
  const [focused, setFocused] = useState(false);
  // enquanto a IA escreve: força a Prévia (leitura) e trava a edição
  const effMode = readOnly ? "view" : mode;
  const taRef = useRef(null);
  const viewRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false); // conteúdo passa da altura recolhida?
  const selRef = useRef(null); // seleção a restaurar após inserir sintaxe

  useLayoutEffect(() => {
    if (selRef.current && taRef.current) {
      const [s, e] = selRef.current;
      taRef.current.focus();
      taRef.current.setSelectionRange(s, e);
      selRef.current = null;
    }
  });

  // mede se o conteúdo cabe; expandido no Editar, a textarea cresce com o texto
  useLayoutEffect(() => {
    const el = effMode === "edit" ? taRef.current : viewRef.current;
    if (!el) return;
    if (effMode === "edit") {
      // "auto" para medir zera a rolagem interna e a da página — guarda e restaura
      const top = el.scrollTop;
      let pageEl = el.parentElement; // container que rola a página (no editor do agente é uma div)
      while (pageEl && !/(auto|scroll)/.test(getComputedStyle(pageEl).overflowY)) pageEl = pageEl.parentElement;
      if (!pageEl) pageEl = document.scrollingElement;
      const pageTop = pageEl ? pageEl.scrollTop : 0;
      el.style.height = "auto";
      const full = el.scrollHeight;
      setOverflows(full > COLLAPSED);
      el.style.height = `${expanded ? full : Math.min(full, COLLAPSED)}px`;
      el.scrollTop = top;
      if (pageEl) pageEl.scrollTop = pageTop;
    } else {
      setOverflows(el.scrollHeight > COLLAPSED);
    }
  }, [value, effMode, expanded]);

  const lineCount = String(value || "").split("\n").length;

  // insere sintaxe: prefixo de LINHA (## , - , 1. , > ) ou WRAP da seleção (**)
  const apply = (kind) => {
    const ta = taRef.current;
    if (!ta) return;
    const v = value || "";
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    if (kind === "bold") {
      const sel = v.slice(s, e) || "texto";
      const next = v.slice(0, s) + "**" + sel + "**" + v.slice(e);
      onChange(next);
      selRef.current = [s + 2, s + 2 + sel.length];
      return;
    }
    const prefix = kind === "h2" ? "## " : kind === "h3" ? "### " : kind === "ul" ? "- " : kind === "ol" ? "1. " : "> ";
    const lineStart = v.lastIndexOf("\n", s - 1) + 1;
    const next = v.slice(0, lineStart) + prefix + v.slice(lineStart);
    onChange(next);
    selRef.current = [s + prefix.length, e + prefix.length];
  };

  const blocks = parse(value);

  return (
    <div className={classes.wrap}>
      <div className={classes.bar}>
        <span className={classes.label}>{label}</span>
        <div className={classes.grow} />
        {readOnly ? (
          <span className={classes.writingChip}>
            <span className={classes.writingDot} /> IA escrevendo…
          </span>
        ) : (
          <div className={classes.seg}>
            <button type="button" className={`${classes.segBtn} ${mode === "edit" ? classes.segOn : ""}`} onClick={() => setMode("edit")}>
              <EditIcon /> Editar
            </button>
            <button type="button" className={`${classes.segBtn} ${mode === "view" ? classes.segOn : ""}`} onClick={() => setMode("view")}>
              <ViewIcon /> Visualizar
            </button>
          </div>
        )}
      </div>

      <div className={`${classes.box} ${effMode === "edit" && focused ? classes.boxFocus : ""}`}>
        {effMode === "edit" ? (
          <>
            <div className={classes.toolbar}>
              <button type="button" className={classes.tbtn} title="Título (H2)" onClick={() => apply("h2")}>H2</button>
              <button type="button" className={classes.tbtn} title="Subtítulo (H3)" onClick={() => apply("h3")}>H3</button>
              <span className={classes.tsep} />
              <button type="button" className={classes.tbtn} title="Negrito" onClick={() => apply("bold")}><BoldIcon /></button>
              <button type="button" className={classes.tbtn} title="Lista" onClick={() => apply("ul")}><BulletIcon /></button>
              <button type="button" className={classes.tbtn} title="Lista numerada" onClick={() => apply("ol")}><NumberIcon /></button>
              <button type="button" className={classes.tbtn} title="Destaque (o que evitar)" onClick={() => apply("callout")}><QuoteIcon /></button>
            </div>
            <textarea
              ref={taRef}
              className={classes.ta}
              value={value || ""}
              onChange={e => onChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={placeholder}
            />
          </>
        ) : (
          <div className={classes.viewWrap} style={!expanded && overflows ? { maxHeight: COLLAPSED } : undefined}>
          <div ref={viewRef} className={classes.view}>
            {blocks.length === 0 && <div className={classes.empty}>{emptyText}</div>}
            {blocks.map((b, i) => {
              if (b.t === "h2") return <div key={i} className={classes.h2}><span className={classes.h2mark} />{inline(b.text)}</div>;
              if (b.t === "h3") return <div key={i} className={classes.h3}>{inline(b.text)}</div>;
              if (b.t === "ul") return <ul key={i} className={classes.ul}>{b.items.map((it, j) => <li key={j} className={classes.li}>{inline(it)}</li>)}</ul>;
              if (b.t === "ol") return <div key={i}>{b.items.map((it, j) => <div key={j} className={classes.olrow}><span className={classes.olnum}>{j + 1}</span><span>{inline(it)}</span></div>)}</div>;
              if (b.t === "callout") return <div key={i} className={classes.callout}>{inline(b.text)}</div>;
              return <p key={i} className={classes.p}>{inline(b.text)}</p>;
            })}
          </div>
          {!expanded && overflows && <div className={classes.fade} />}
          </div>
        )}
        {overflows && (
          <button type="button" className={classes.more} onClick={() => setExpanded(x => !x)}>
            {expanded ? <><CollapseIcon /> Recolher</> : <><ExpandIcon /> Mostrar tudo ({lineCount} linhas)</>}
          </button>
        )}
      </div>

      <div className={classes.hint}>
        {readOnly ? hintWriting : effMode === "edit" ? hintEdit : hintView}
      </div>
    </div>
  );
};

export default PersonaEditor;
